import { DestinationInterface } from '../interfaces/Account/DestinationInterface'
import { BusinessUnitInterface } from '../interfaces/BusinessUnit/BusinessUnitInterface'
import { DeliveryEnum } from '../interfaces'
import { ShipmentGroup } from '../interfaces/Import/ShipmentGroupInterface'

export interface SimpleResponseLike<T> {
  didError: boolean
  errorMessage?: string
  model: T | null
}

export type GetBusinessUnitByTerritoryFn = (
  lng: number,
  lat: number
) => Promise<SimpleResponseLike<BusinessUnitInterface>>

export interface DestinationApplyResult {
  patch: Partial<ShipmentGroup>
  warning?: string
}

// Aviso único (fuente de verdad compartida por Review y AccordionRow) cuando unas
// coordenadas caen fuera de la zona de cobertura. No se limpian las coordenadas ni se
// resuelve por la ciudad del archivo: se le pide al operador que las corrija.
export const COORD_OUT_OF_ZONE_MSG =
  'Coordenadas fuera de zona de cobertura. Ingresa coordenadas o usa "Búsqueda en Google Maps:"'

/**
 * Mensaje del bloqueo inicial de carga masiva: qué le falta a la cuenta para poder
 * cargar guías (oficinas origen, servicios autorizados, cuenta para facturar).
 *
 * Enumera en español natural — "a", "a ni b", "a, b ni c" — porque las condiciones
 * pueden faltar en cualquier combinación y antes había un caso especial escrito a mano
 * solo para dos, que dejaba de cubrir el resto al agregar una tercera.
 * Devuelve '' con la lista vacía (nada que bloquear).
 */
export const missingSetupMessage = (missing: string[]): string => {
  if (!missing || missing.length === 0) return ''
  const listado =
    missing.length === 1
      ? missing[0]
      : `${missing.slice(0, -1).join(', ')} ni ${missing[missing.length - 1]}`
  return `No tiene ${listado}. Por favor comuníquese con el departamento de Soporte Técnico.`
}

// Venezuelan mobile area codes (without leading 0).
const MOBILE_PREFIXES = ['412', '414', '416', '422', '424', '426']

// Normalize phone to canonical storage format aligned with AccordionRow's setPhoneCombined:
//   - Mobile: 10 digits, NO leading 0 (e.g., "4141234567" = 414 + 1234567)
//   - FIJO: leading 0 + 7-10 digits = 8-11 digits total (e.g., "02121234567")
//
// G.1 fix: previously only 11-digit-leading-0 mobile got stripped, leaving FIJO numbers
// without leading 0 stored as bare digits → validation flagged "Operadora requerida"
// even though the UI showed FIJO. Now also prepends 0 to FIJO numbers that arrive
// without one, so storage convention stays consistent.
export const normalizePhone = (raw: string): string => {
  let digits = (raw || '').replace(/\D/g, '')
  if (!digits) return ''

  // Código de país pegado al número ("+58 412 1234567", "+58 04121234567"): un
  // número local nunca pasa de 11 dígitos, así que un 58 adelante con más que
  // eso es el +58 y se descarta. Sin esto, el resto no matcheaba ningún prefijo
  // móvil y el 0 que se antepone abajo lo disfrazaba de FIJO.
  if (digits.length > 11 && digits.startsWith('58')) {
    digits = digits.substring(2)
  }

  // Móvil con 0 adelante (0412/0424/…): se quita el 0 y queda el resto, SIN
  // exigir largo exacto. Antes solo se quitaba con 11 dígitos justos, así que
  // un móvil con un dígito de más o de menos conservaba el 0 y el editor lo
  // tomaba como FIJO; ahora queda del lado móvil y es el validador el que dice
  // "incompleto" o "demasiado largo", que es el mensaje correcto.
  if (
    digits.startsWith('0') &&
    MOBILE_PREFIXES.includes(digits.substring(1, 4))
  ) {
    return digits.substring(1) // canonical mobile: no leading 0
  }

  // No leading 0
  if (!digits.startsWith('0')) {
    // Canonical mobile (10 digits starting with mobile prefix): keep as is
    if (digits.length >= 3 && MOBILE_PREFIXES.includes(digits.substring(0, 3))) {
      return digits
    }
    // FIJO without leading 0 → prepend so UI/validation know it's FIJO
    return '0' + digits
  }

  return digits // FIJO with its leading 0
}

// Dropdown values in the Excel template are generated as "${buCode} - ${buArea}"
// (space-hyphen-space). Split on that exact separator so buCodes containing internal
// hyphens (e.g. "1102-CC") survive the parse. Fall back to the trimmed input when no
// separator exists.
export const STORE_CODE_SEPARATOR = ' - '
export const cleanStoreCode = (str: string): string => {
  if (!str) return ''
  const sepIdx = str.indexOf(STORE_CODE_SEPARATOR)
  if (sepIdx !== -1) return str.substring(0, sepIdx).trim()
  return str.trim()
}

// Build the destination patch from a favorite. If isHomeDelivery + lat/lng valid,
// re-validate territory via getBusinessUnitByTerritory and override store/city/postal
// with territory result. If territory invalid, keep favorito data and surface warning.
export const applyDestinationFromFavorite = async (
  fav: DestinationInterface,
  getTerritory?: GetBusinessUnitByTerritoryFn
): Promise<DestinationApplyResult> => {
  const acc = fav.account
  const loc = fav.location
  const bu = fav.businessUnit
  const phoneRaw = acc?.listAccountPhone?.[0]?.phoneNumber || ''
  const patch: Partial<ShipmentGroup> = {
    consigneeDocType:
      acc?.taxIdentificationTypeID == null ? '' : String(acc.taxIdentificationTypeID),
    consigneeDocNumber: acc?.identificationNumber || '',
    consigneeName: acc?.accountFullName || '',
    consigneePhone: normalizePhone(phoneRaw),
    consigneeEmail: acc?.listAccountEmail?.[0]?.email || '',
    destinationAddress: loc?.address || '',
    postalCode: loc?.postalCode || '',
    // V3+: lat/lng only apply to home delivery. Office favorites stay coordinate-free.
    latitude: fav.isHomeDelivery ? loc?.coordinates?.lat ?? null : null,
    longitude: fav.isHomeDelivery ? loc?.coordinates?.lng ?? null : null,
    storeCode: bu?.buCode || '',
    destinationCity: bu?.location?.locationCode || '',
    deliveryType: fav.isHomeDelivery ? DeliveryEnum.DELIVERY : DeliveryEnum.OFFICE,
    favoriteDestinationId: fav.id || acc?.id || '',
    // H.3d: punto de referencia from the favorite (operator's own note like
    // "Frente al parque"). Only meaningful for home delivery — office BU lookup
    // doesn't carry an address reference.
    addressReference: fav.isHomeDelivery ? loc?.reference || '' : '',
  }

  if (
    fav.isHomeDelivery &&
    patch.latitude != null &&
    patch.longitude != null &&
    getTerritory
  ) {
    try {
      const res = await getTerritory(patch.longitude, patch.latitude)
      if (!res.didError && res.model) {
        patch.storeCode = res.model.buCode || patch.storeCode
        patch.destinationCity =
          res.model.location?.locationCode || patch.destinationCity
        patch.postalCode = res.model.location?.postalCode || patch.postalCode
      } else {
        return {
          patch,
          warning: `Favorito ${acc?.accountFullName || ''}: territorio cambió, usando datos del favorito.`,
        }
      }
    } catch (_) {
      // Silent — keep favorito data
    }
  }

  return { patch }
}

export const clearConsigneeFields = (): Partial<ShipmentGroup> => ({
  consigneeDocType: '',
  consigneeDocNumber: '',
  consigneeName: '',
  consigneePhone: '',
  consigneeEmail: '',
  destinationAddress: '',
  postalCode: '',
  latitude: null,
  longitude: null,
  storeCode: '',
  destinationCity: '',
  favoriteDestinationId: undefined,
})

// Phone normalize-on-paste — accepts:
//   "0414 1234567"   "0414-1234567"   "04141234567"
//   "414 1234567"    "414-1234567"    "4141234567"
// Returns { code: 3-digit, number: 7-digit } if matched against prefixList, else null.
const PHONE_PASTE_REGEX = /^[\s-]*(\d{3,4})[\s-]?(\d{7})$/

export const tryParsePastedPhone = (
  raw: string,
  prefixList: { value: string }[]
): { code: string; number: string } | null => {
  const cleaned = (raw || '').trim()
  const match = cleaned.match(PHONE_PASTE_REGEX)
  if (!match) return null
  const [, codeRaw, number] = match
  // Try matching with and without leading 0
  const stripped = codeRaw.length === 4 && codeRaw.startsWith('0')
    ? codeRaw.substring(1)
    : codeRaw
  const found = prefixList.find((p) => p.value === stripped)
  if (!found) return null
  return { code: found.value, number }
}

// Tolerant parser for "RIF — Nombre" / "RIF – Nombre" / "RIF - Nombre".
// Accepts em-dash (—), en-dash (–), or hyphen (-) as separator.
// Returns the RIF part (everything before first separator) trimmed.
export const parseFavoriteIdentifier = (display: string): string => {
  if (!display) return ''
  // Split on space + separator + space (preferred) OR any of the dashes alone
  const sepRegex = /\s+[—–]\s+|\s+-\s+/
  const parts = display.split(sepRegex)
  if (parts.length > 1) return parts[0].trim()
  // Fallback: no clear separator, return whole string
  return display.trim()
}

// H.4: defensive coordinate extractor for SheetJS cell values. Handles:
//   - Number (the common case when Excel parses comma-decimal locale strings)
//   - string with comma or period decimal (es-VE vs en-US locale)
//   - Excel rich cell objects: { result: Number } or { value: ... } (formulas)
//   - null/undefined/empty → null (caller decides what to do)
// Returns null only for genuinely unparseable input. Preserves 0,0 (equator/meridian).
export const extractCoord = (raw: unknown): number | null => {
  if (raw == null || raw === '') return null
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw
  if (typeof raw === 'object') {
    const obj = raw as { result?: unknown; value?: unknown }
    if (typeof obj.result === 'number' && Number.isFinite(obj.result)) {
      return obj.result
    }
    if ('value' in obj) return extractCoord(obj.value)
    return null
  }
  const s = String(raw).trim().replace(',', '.')
  if (!s) return null
  const n = parseFloat(s)
  return Number.isFinite(n) ? n : null
}

// Reverse-geocode lat/lng to a customer-facing address via Google Maps Geocoder.
// getBusinessUnitByTerritory returns the TEALCA store's address (warehouse), not
// the customer's location at the coords — so for autofilling destinationAddress
// we need a true reverse-geocode. Mirrors the path in FormMaps.handleCoordsSelect:
// prefer street_address/route/intersection results; fall back to first result.
// Returns null when google.maps isn't loaded, no results, or on error — caller
// should fall back to keeping the prior address.
export const reverseGeocodeCoords = (
  lat: number,
  lng: number
): Promise<{ address: string; postalCode: string } | null> => {
  return new Promise((resolve) => {
    const g = (window as any).google
    if (!g?.maps?.Geocoder) {
      resolve(null)
      return
    }
    try {
      const geocoder = new g.maps.Geocoder()
      geocoder.geocode(
        { location: new g.maps.LatLng(lat, lng) },
        (results: any[] | null, status: string) => {
          if (status !== 'OK' || !results || results.length === 0) {
            resolve(null)
            return
          }
          const preferred = results.filter((r) =>
            r.types?.includes('street_address') ||
            r.types?.includes('route') ||
            r.types?.includes('intersection')
          )
          const selected = preferred[0] ?? results[0]
          const postalCode =
            selected.address_components?.find((c: any) =>
              c.types?.includes('postal_code')
            )?.long_name ?? ''
          resolve({
            address: selected.formatted_address ?? '',
            postalCode,
          })
        }
      )
    } catch (_) {
      resolve(null)
    }
  })
}

// Match a destination against a query against displayName, accountFullName,
// identificationNumber, or first phone number. Used by FavoriteDestinationPicker
// and AccountSearch. Empty query returns true (full list).
export const filterDestinationByQuery = (
  d: DestinationInterface,
  query: string
): boolean => {
  const q = (query || '').trim().toLowerCase()
  if (!q) return true
  const acc = d.account
  if (!acc) return false
  if ((acc.displayName || '').toLowerCase().includes(q)) return true
  if ((acc.accountFullName || '').toLowerCase().includes(q)) return true
  if ((acc.identificationNumber || '').toLowerCase().includes(q)) return true
  const phone = acc.listAccountPhone?.[0]?.phoneNumber || ''
  if (phone.includes(q)) return true
  return false
}
